import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable, catchError, throwError, switchMap, of } from 'rxjs';
import { AuthService } from "../services/auth.service";
import { Router } from '@angular/router';
import { EventLogInvalidUserCreateRequest } from '../models/viewmodels';
import { EventLogService } from '../services/event-log.service';
import { v7 as uuidv7 } from 'uuid';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private eventLogService: EventLogService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const requestToHandle = req.url.toLowerCase().includes('powerplatform.com')
      ? req
      : this.addAuthenticationToken(req);

    //add next code here using switch map
    return next.handle(
      //take the request and add the authentication token and pass to next handler
      requestToHandle
    ).pipe(
    
      // switch map
      switchMap((respEvent: HttpEvent<any>) => {
        let hasError: boolean = false;


          // Check if the event is an HttpResponse
          if (respEvent instanceof HttpResponse) {
              // You can process the response here if needed
              let authHeader = respEvent.headers.get('Authorization');
              if (respEvent.status === 200 && respEvent.url?.toLowerCase().includes('powerplatform.com')) {
                //save token to local storage
                let chatBotToken = respEvent.body['token'];
                let chatBotConversationId = respEvent.body['conversationId'];
                //localStorage.setItem('ChatBot-AuthToken', authHeader);
                localStorage.setItem('ChatBot-Token', chatBotToken);
                localStorage.setItem('ChatBot-ConversationId', chatBotConversationId);
              } else if (respEvent.status === 200 && authHeader != null) {

                //console.log('AuthInterceptor', 'intercept', 'response has authorization header', respEvent.url, respEvent.headers.get('Authorization'));

                let originalDecodedToken = this.authService.getDecodedAccessToken();
                let newToken = respEvent.headers.get('Authorization')?.replace('Bearer ', '');
                let newDecodedToken = this.authService.decodeToken(newToken);

                let originalUserId:string = originalDecodedToken?.UserId?.trim() ?? '';
                let newUserId:string = newDecodedToken?.UserId?.trim() ?? '';
                let impersonatingUserId = localStorage.getItem('ImpersonatingUserId')?.trim() ?? '';

                //simulate 1 request going bad
                //const randomBool = () => !!Math.round(Math.random());
                //let randomTest = randomBool();
                //console.log('RANDOM BOOL:', randomTest);
                //if (randomTest && !respEvent.url?.toLocaleLowerCase().includes('login')) {
                //     newUserId = "99";
                //}

                // user is impersonating
                if (impersonatingUserId !== null && impersonatingUserId.length > 0 && (newUserId == impersonatingUserId || impersonatingUserId == '???')) {
                  //console.log('impersonation', impersonatingUserId, newUserId);
                  if (!this.isNoAuthEndpoint(respEvent.url)) {
                    this.authService.token = respEvent.headers.get('Authorization');
                  }

                } else if (originalUserId != '' && newUserId != '' && originalUserId !== newUserId) {
                  //user ids do not match

                  try { 

                    var evt: EventLogInvalidUserCreateRequest = {
                      ExpectedValue: Number(originalUserId),
                      ActualValue: Number(newUserId),
                      RequestUrl: respEvent.url,
                      LocationUrl: window.location.href
                    };
                    this.eventLogService.createInvalidUserLogoutEvent(evt);

                  } catch (e) {
                    console.error('error when logging invalid user', e, originalDecodedToken, newDecodedToken);
                  }

                  //only throw error if not a no-auth endpoint
                  if (!this.isNoAuthEndpoint(respEvent.url)) {
                    //console.log('AuthInterceptor', 'intercept', 'authd response has new token with different user id, throwing error', originalDecodedToken, newDecodedToken, respEvent.url);

                    //performa a second try if the request has not already been retried and has the httpretry2uuid param
                    if (!respEvent.url.includes('httpretry2uuid=')) {

                      let newRequestId = uuidv7();
                      let retryUrl = respEvent.url;
                      // If url already has a query string, append with &
                      if (retryUrl.indexOf('?') > -1) { 
                         retryUrl += '&httpretry2uuid=' + newRequestId;
                      } else {
                         retryUrl += '?httpretry2uuid=' + newRequestId;
                      }
                      let retryReq = req.clone({
                         url: retryUrl
                      });

                      //console.log('AuthInterceptor', 'intercept', 'retrying request once with new request id', retryUrl);
                      let nextReq = this.addAuthenticationToken(retryReq);
                      return next.handle(nextReq); // next.handle(nextReq);

                    } else {
                       hasError = true;
                    }
                  }
                } else {
                  //console.log('valid user', originalUserId, newUserId);
                  if (!this.isNoAuthEndpoint(respEvent.url) && newUserId != '' && authHeader != null) {
                    //console.log('Saving Auth Header', authHeader, respEvent.url);
                    this.authService.token = authHeader;
                  }              

              }
              
            } else if (respEvent.status === 401 || respEvent.status === 403) {
              //console.log('AuthInterceptor', 'intercept', 'response not okay', 'res', respEvent.status, respEvent.url);
              this.authService.logout(); //will remove local storage and redirect user to login
              //this.router.navigate(['/user/user-login']);
            } 

            if (hasError) {
              throw new Error('Invalid User - Event Logged');
            }

        }
        // Pass the event along
        return of(respEvent);
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );

  }

  private addAuthenticationToken(req: HttpRequest<any>): HttpRequest<any> {
    //return new request object with access token in header
    let clonedReq: HttpRequest<any>;

    // Get access token from AuthService
    //console.log('addAuthenticationToken - current token:', this.authService.token);
    let token = this.authService.token ?? '';
    if (token.startsWith('Bearer ')) {
      token = token.replace('Bearer ', ''); // Remove 'Bearer ' prefix if it exists
    } 

    let xRequestId = uuidv7();

    if (req.headers !== null && req.headers?.get('Content-Type') === 'multipart/form-data') {
      //console.log('sending multipart/form-data request');
      clonedReq = req.clone({
          //headers: req.headers.set('Authorization', token),
          //withCredentials: true,  //todo when HTTPS is enabled
          //NOTE: if HTTPONLY, token should already be in the header.  We just need to pass with it "withCredentials"
          //https://blog.angular-university.io/angular-jwt-authentication/
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + token,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Request-ID': xRequestId  
        }),
        //params: req.params.set('httprequuid', paramRequestId), //temporary add to params to avoid TS error below
      });

    } else if (req.headers !== null && req.headers.get('Content-Type') !== null) {
      //console.log('sending request with Content-Type:', req.headers.get('Content-Type'));
      clonedReq = req.clone({
          //headers: req.headers.set('Authorization', token),
          //withCredentials: true,  //todo when HTTPS is enabled
          //NOTE: if HTTPONLY, token should already be in the header.  We just need to pass with it "withCredentials"
          //https://blog.angular-university.io/angular-jwt-authentication/
        headers: new HttpHeaders({
          'Content-Type': req.headers.get('Content-Type') ?? 'application/json',
          Authorization: 'Bearer ' + token,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Request-ID': xRequestId  
        }),
        //params: req.params.set('httprequuid', paramRequestId), //temporary add to params to avoid TS error below
      });
    } else {
      //console.log('sending request with default Content-Type: application/json');

      clonedReq = req.clone({
          //headers: req.headers.set('Authorization', token),
          //withCredentials: true,  //todo when HTTPS is enabled
          //NOTE: if HTTPONLY, token should already be in the header.  We just need to pass with it "withCredentials"
          //https://blog.angular-university.io/angular-jwt-authentication/
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Request-ID': xRequestId  
        }),
        //params: req.params.set('httprequuid', paramRequestId), //temporary add to params to avoid TS error below
      });
    }

    //console.log('clonedReqUrl with request id:', clonedReq.params.has('httprequuid'));
    if (!clonedReq.params.has('httprequuid') && !clonedReq.url.includes('httprequuid=')) {
      let paramRequestId = uuidv7();
      clonedReq = clonedReq.clone({
        params: clonedReq.params.set('httprequuid', paramRequestId)
      });
    }

    //console.log('AuthInterceptor', 'addAuthenticationToken', 'added authentication token to request:', token);
    //console.log('AuthInterceptor', 'addAuthenticationToken', 'headers on req', clonedReq.headers.keys(), clonedReq.headers.get('Authorization'));
    //console.log('AuthInterceptor', 'addAuthenticationToken', 'final request url with request id:', clonedReq.headers);
    //console.log('Returning cloned request with authentication token:', clonedReq);

    return clonedReq;
  }

  // Only endpoints that intentionally change identity or issue tokens
  // are allowed to rotate Authorization headers.
  private isNoAuthEndpoint(url: string): boolean {
    const u = (url || '').toLowerCase();
    return u.includes('/api/brand');
  }

  // cns: this is a copy of the handleError from the error-interceptor
  handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      console.error('An error occurred:', error);
    } else {
      console.error(
        // The backend returned an unsuccessful response code.
        // The response body may contain clues as to what went wrong.
        `Backend returned error. \nCode : ${error?.status},\nMessage : ${error.message}\nBody : `,
        error?.error
      );
      if (error.status === 401 || error.status === 403) {
        console.error('AuthInterceptor', 'handleError', 'error - about to redirect to login', error);
        this.router.navigate(['/user/user-login']);
      }
    }
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }


}
